/**
 * Figures of the client portal sections (W5): one place reads a call's date,
 * its outcome, whether it reached a human, a duration and an amount, so the
 * Calls, Meetings and Performance tabs say the same thing.
 *
 * Rows come from two shapes: ClientDashboard's merged rows (`call_date`,
 * `outcome`, `is_connected`, `_agent`) and the dialer preview's raw
 * ParallelCalls (`started_at`, `call_outcome`, `agent_connected_at`,
 * `agent_name`). Metric contract (W2, AnalyticsService): connected =
 * `agent_connected_at`, meeting = isMeetingBooked, conversion = meetings /
 * connected (null without a connected call, shown « — »).
 */
import { hasMeetingBooked } from '@/utils/callUtils';
import { conversionRate } from '@/lib/metrics';

export interface PortalCall {
  id?: string;
  call_date?: string | Date | null;
  started_at?: string | Date | null;
  created_date?: string | Date | null;
  outcome?: string | null;
  call_outcome?: string | null;
  call_status?: string | null;
  meeting_booked?: boolean | null;
  is_connected?: boolean | null;
  agent_connected_at?: string | Date | null;
  duration_seconds?: number | null;
  _agent?: string | null;
  agent_name?: string | null;
  _source?: string | null;
}

/**
 * REV-W5-1: where the calls given to a section stand. While they load or
 * when they failed, a section shows a skeleton or « Indisponible » with
 * « Réessayer », never 0 nor « Aucun appel ». Without it (the dialer
 * preview, which gets loaded calls) a section is `ready`.
 */
export type SectionStatus = 'loading' | 'error' | 'ready';
export interface SectionLoad {
  status: SectionStatus;
  /** The server's error code, shown under the message (DES-16). */
  code?: string;
  onRetry?: () => void;
}
export const READY: SectionLoad = { status: 'ready' };

export function callDate(c: PortalCall): Date | null {
  const raw = c.call_date ?? c.started_at ?? c.created_date;
  if (!raw) return null;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function callOutcome(c: PortalCall): string {
  return c.call_outcome || c.outcome || '';
}

export function callAgent(c: PortalCall): string {
  const a = c._agent || c.agent_name || '';
  return a === '—' ? '' : a;
}

export function isConnectedCall(c: PortalCall): boolean {
  if (c.agent_connected_at) return true;
  return c.is_connected === true;
}

export function isMeeting(c: PortalCall): boolean {
  return hasMeetingBooked(c);
}

export interface CallFigures {
  calls: number;
  connected: number;
  meetings: number;
  callbacks: number;
  /** Meetings / connected; null when nothing connected. */
  conversion: number | null;
}

export function callFigures(rows: PortalCall[]): CallFigures {
  let connected = 0;
  let meetings = 0;
  let callbacks = 0;
  for (const c of rows) {
    if (isConnectedCall(c)) connected += 1;
    if (isMeeting(c)) meetings += 1;
    const o = callOutcome(c);
    if (o === 'callback_scheduled' || o === 'callback_requested') callbacks += 1;
  }
  return { calls: rows.length, connected, meetings, callbacks, conversion: conversionRate(meetings, connected) };
}

/** « 2:05 » for 125 s, « — » for nothing. */
export function formatDuration(seconds: number | null | undefined): string {
  if (!seconds || seconds <= 0) return '—';
  const s = Math.round(seconds);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** An amount in euros, in the reader's language (« 12 500 € » / « €12,500 »). */
export function formatMoney(amount: number, locale?: string): string {
  return new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(amount);
}

/** Calls whose date falls in [start, end]. */
export function inRange<T extends PortalCall>(rows: T[], start: Date, end: Date): T[] {
  const a = start.getTime();
  const b = end.getTime();
  return rows.filter((c) => {
    const d = callDate(c);
    return !!d && d.getTime() >= a && d.getTime() <= b;
  });
}
